import { elementText, isHidden, nearestHeading } from './domText';

// DataTables clones the header/footer of scrolling tables into separate
// <table>s; the data table itself is the one inside .dataTables_scrollBody.
const CLONE_SELECTOR = '.dataTables_scrollHead, .dataTables_scrollFoot';

const rowCells = (tr) => {
  const cells = [];
  Array.from(tr.cells || []).forEach((cell) => {
    cells.push(elementText(cell));
    const span = Number(cell.getAttribute('colspan')) || 1;
    for (let i = 1; i < span; i += 1) cells.push('');
  });
  return cells;
};

const isEmptyRow = (cells) => cells.every((c) => c === '');

const htmlToText = (doc, html) => {
  if (typeof html !== 'string') return html == null ? '' : String(html);
  const div = doc.createElement('div');
  div.innerHTML = html;
  return elementText(div);
};

const dataTablesApi = (table) => {
  const $ = table.ownerDocument.defaultView?.jQuery;
  if (!$?.fn?.dataTable?.isDataTable?.(table)) return null;
  const api = $(table).DataTable();
  // Server-side tables only hold the visible page, which is what the DOM has.
  return api.page.info().serverSide ? null : api;
};

/**
 * Client-side DataTables keep every filtered row in memory while the DOM
 * shows a single page; export the whole filtered, sorted set.
 */
const readDataTable = (api, doc) => {
  const header = api
    .columns()
    .header()
    .toArray()
    .map((th) => elementText(th));
  const selection = api.rows({ search: 'applied', order: 'applied' });
  const nodes = selection.nodes().toArray();
  const body = nodes.every(Boolean)
    ? nodes.map(rowCells)
    : selection
        .data()
        .toArray()
        .map((d) => (Array.isArray(d) ? d : Object.values(d)).map((v) => htmlToText(doc, v)));
  return { headerRows: [header], bodyRows: body };
};

const readDomTable = (table) => {
  const headerRows = [];
  const bodyRows = [];
  Array.from(table.rows).forEach((tr) => {
    if (tr.closest('table') !== table) return;
    const cells = rowCells(tr);
    if (isEmptyRow(cells)) return;
    const inHead = tr.parentElement?.tagName === 'THEAD';
    (inHead ? headerRows : bodyRows).push(cells);
  });
  return { headerRows, bodyRows };
};

/**
 * Data tables currently rendered in a SEAL result document, as text exactly
 * as displayed. Layout tables (those wrapping other tables) are skipped in
 * favour of the tables they contain.
 *
 * @returns {{ title: string, rows: string[][], headerRowCount: number }[]}
 */
export const extractTables = (doc) => {
  const tables = Array.from(doc.querySelectorAll('table')).filter(
    (table) =>
      !table.closest(CLONE_SELECTOR) &&
      !table.querySelector('table') &&
      !isHidden(table),
  );
  const result = [];
  tables.forEach((table) => {
    const api = dataTablesApi(table);
    const { headerRows, bodyRows } = api ? readDataTable(api, doc) : readDomTable(table);
    const rows = [...headerRows, ...bodyRows];
    if (rows.length === 0) return;
    const titleSource = table.closest('.dataTables_wrapper') || table;
    const caption = table.caption ? elementText(table.caption) : '';
    result.push({
      title: caption || nearestHeading(titleSource),
      rows,
      headerRowCount: headerRows.length,
    });
  });
  return result;
};
